/*
  Phase 2b — Reactive compat checker.

  Subscribes to:
    - trautis:variant-change   (from 2a's product-variant-reactivity-trautis.js)
    - trautis:bmw-changed      (from 1d's trautis-fitment.js)

  Resolves one of four states (match / variant-mismatch / product-mismatch / unset)
  and toggles `hidden` on the SSR-rendered state divs from snippet-compat-checker.

  Matcher: TrautisFitment.matches(gids, bmw, lookupData) — the 3-arg sync form.
  Lookup is loaded once via TrautisFitment.loadLookup() (cached Promise) before
  resolveState runs, so the resolveState helper itself stays synchronous.
*/

(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  } else {
    root.TrautisCompatChecker = api;
    if (root.document && root.document.addEventListener) {
      if (root.document.readyState === 'loading') {
        root.document.addEventListener('DOMContentLoaded', api.init);
      } else {
        api.init();
      }
    }
  }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  function findVariantById(product, id) {
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].id === id) return product.variants[i];
    }
    return null;
  }

  function variantFitmentGids(variant, product) {
    if (variant.fitmentGids && variant.fitmentGids.length > 0) return variant.fitmentGids;
    return product.productFitmentGids || [];
  }

  function describeSiblingDiff(current, sibling) {
    var parts = [];
    for (var i = 0; i < sibling.options.length; i++) {
      if (sibling.options[i] !== current.options[i]) parts.push(sibling.options[i]);
    }
    return parts.join(' / ');
  }

  // matcher(gids) liefert den matches()-String: 'match' | 'engine-unconfirmed'
  // | 'no-match' | 'unknown'. Präzedenz: eigener match > Sibling-match
  // (variant-mismatch) > engine-unconfirmed (egal wo) > product-mismatch.
  function resolveState(currentVariant, garage, product, matcher) {
    if (!garage) return { state: 'unset', matchedVariant: null };

    var curGids = variantFitmentGids(currentVariant, product);
    var curRes = curGids.length > 0 ? matcher(curGids) : 'unknown';
    if (curRes === 'match') {
      return { state: 'match', matchedVariant: currentVariant };
    }

    // sawAnyData: gab es überhaupt Fitment-GIDs zum Bewerten? Ohne jegliche Daten
    // (weder Varianten- noch Produkt-Ebene) ist „Passt nicht" eine unzulässige
    // Behauptung — dann neutraler 'no-data'-Zustand statt definitivem Mismatch.
    var sawAnyData = curGids.length > 0;
    var sawUnconfirmed = curRes === 'engine-unconfirmed';
    for (var i = 0; i < product.variants.length; i++) {
      var sib = product.variants[i];
      if (sib.id === currentVariant.id) continue;
      var sibGids = variantFitmentGids(sib, product);
      if (sibGids.length === 0) continue;
      sawAnyData = true;
      var sibRes = matcher(sibGids);
      if (sibRes === 'match') {
        return { state: 'variant-mismatch', matchedVariant: sib };
      }
      if (sibRes === 'engine-unconfirmed') sawUnconfirmed = true;
    }

    if (sawUnconfirmed) return { state: 'engine-unconfirmed', matchedVariant: null };
    if (!sawAnyData) return { state: 'no-data', matchedVariant: null };
    return { state: 'product-mismatch', matchedVariant: null };
  }

  function getCompatData(checkerEl) {
    var s = checkerEl.querySelector('script[type="application/json"][data-compat-data]');
    if (!s) return null;
    try { return JSON.parse(s.textContent); } catch (e) { return null; }
  }

  function showState(checkerEl, stateName) {
    var states = checkerEl.querySelectorAll('[data-ckr-state]');
    states.forEach(function (el) {
      if (el.getAttribute('data-ckr-state') === stateName) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  // Glow-Ton fürs beruhigende Aufleuchten nach der Fahrzeugwahl: grün wenn das
  // Teil passt, sonst dezent-neutral — NIE alarmrot (Ziel: Angst nehmen, nicht
  // erzeugen). null = kein Glow (z.B. unset).
  function glowToneForState(state) {
    if (state === 'match') return 'match';
    if (state === 'variant-mismatch' || state === 'product-mismatch' ||
        state === 'engine-unconfirmed' || state === 'no-data') return 'neutral';
    return null;
  }

  var GLOW_CLASSES = ['t-compat-checker--glow-match', 't-compat-checker--glow-neutral'];

  // Sanftes, langsames Aufleuchten (CSS) NACH dem Auflösen des Ergebnisses.
  // Reflow erzwingen, damit es auch bei erneuter Auswahl neu startet; Klasse
  // nach der Animation wieder entfernen.
  function applyGlow(checkerEl, state) {
    var tone = glowToneForState(state);
    GLOW_CLASSES.forEach(function (c) { checkerEl.classList.remove(c); });
    if (!tone) return;
    void checkerEl.offsetWidth;
    checkerEl.classList.add('t-compat-checker--glow-' + tone);
    window.setTimeout(function () {
      checkerEl.classList.remove('t-compat-checker--glow-' + tone);
    }, 2800);
  }

  function updateMatchState(checkerEl, garageLabel) {
    var stateEl = checkerEl.querySelector('[data-ckr-state="match"]');
    if (!stateEl) return;
    var titleEl = stateEl.querySelector('[data-ckr-title]');
    var subEl = stateEl.querySelector('[data-ckr-sub]');
    if (titleEl) titleEl.textContent = 'Passt zu deinem ' + (garageLabel || 'Fahrzeug');
    if (subEl) subEl.textContent = '';
  }

  function updateVariantMismatch(checkerEl, currentVariant, matchedVariant) {
    var stateEl = checkerEl.querySelector('[data-ckr-state="variant-mismatch"]');
    if (!stateEl) return;
    var subEl = stateEl.querySelector('[data-ckr-sub]');
    var switchBtn = stateEl.querySelector('[data-ckr-switch]');
    var diff = describeSiblingDiff(currentVariant, matchedVariant);
    if (subEl) subEl.textContent = 'Eine andere Variante passt zu deinem Fahrzeug.';
    if (switchBtn) {
      switchBtn.textContent = '→ Auf "' + (diff || 'kompatible Variante') + '" wechseln';
      switchBtn.setAttribute('data-target-variant-id', String(matchedVariant.id));
    }
  }

  // Build the "Kompatible Teile anzeigen" link from ALL resolved fitment GIDs,
  // not just gids[0]. resolveFilterIds returns the union [engine, 'any'] — Shopify
  // ORs repeated metafield filter values, so motorunabhängige ("any") Teile bleiben
  // sichtbar. gids[0] allein hätte sie verschluckt (gleicher Bug wie der BMW-Toggle).
  function buildFitmentCollectionUrl(gids) {
    if (!Array.isArray(gids) || gids.length === 0) return null;
    var seen = {};
    var parts = [];
    gids.forEach(function (gid) {
      if (gid && !seen[gid]) {
        seen[gid] = true;
        parts.push('filter.p.m.custom.fitment_applications=' + encodeURIComponent(gid));
      }
    });
    if (parts.length === 0) return null;
    return '/collections/all?' + parts.join('&');
  }

  function updateProductMismatch(checkerEl, garageLabel, active) {
    var stateEl = checkerEl.querySelector('[data-ckr-state="product-mismatch"]');
    if (!stateEl) return;
    var titleEl = stateEl.querySelector('[data-ckr-title]');
    var linkEl = stateEl.querySelector('[data-ckr-collection-link]');
    if (titleEl) titleEl.textContent = 'Passt nicht zu deinem ' + (garageLabel || 'Fahrzeug');
    if (active && linkEl && window.TrautisFitment && typeof window.TrautisFitment.resolveFilterIds === 'function') {
      window.TrautisFitment.resolveFilterIds(active.chassis, active.engine).then(function (gids) {
        var href = buildFitmentCollectionUrl(gids);
        if (href) linkEl.href = href;
      });
    }
  }

  function updateEngineUnconfirmed(checkerEl, active) {
    var stateEl = checkerEl.querySelector('[data-ckr-state="engine-unconfirmed"]');
    if (!stateEl) return;
    var titleEl = stateEl.querySelector('[data-ckr-title]');
    if (titleEl) {
      titleEl.textContent = 'Passt zum ' + ((active && active.chassis) || 'Chassis') + ' — motorabhängig';
    }
  }

  function bindSwitchLink(checkerEl, sectionRoot) {
    var btn = checkerEl.querySelector('[data-ckr-switch]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var targetId = parseInt(btn.getAttribute('data-target-variant-id'), 10);
      if (!targetId) return;
      // Set <select>s to the target variant's options, fire change
      var data = getCompatData(checkerEl);
      if (!data) return;
      var target = findVariantById(data, targetId);
      if (!target) return;
      var form = sectionRoot.querySelector('[data-product-form]');
      if (!form) return;
      var selects = form.querySelectorAll('select[data-variant-option]');
      selects.forEach(function (s, idx) {
        if (target.options[idx] != null) {
          s.value = target.options[idx];
          s.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  function openBmwSelector(detail) {
    document.dispatchEvent(new CustomEvent('trautis:open-bmw-selector', { detail: detail || {} }));
  }

  function bindBmwModalCta(checkerEl) {
    var btn = checkerEl.querySelector('[data-ckr-open-bmw-modal]');
    if (!btn) return;
    btn.addEventListener('click', function () { openBmwSelector({}); });
  }

  function bindChooseEngine(checkerEl) {
    var btn = checkerEl.querySelector('[data-ckr-choose-engine]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var active = (window.TrautisFitment && window.TrautisFitment.get) ? window.TrautisFitment.get() : null;
      openBmwSelector({ step: 'engine', chassis: active && active.chassis });
    });
  }

  function applyResult(checkerEl, result, currentVariant, garageLabel, active) {
    if (result.state === 'match') updateMatchState(checkerEl, garageLabel);
    else if (result.state === 'variant-mismatch') updateVariantMismatch(checkerEl, currentVariant, result.matchedVariant);
    else if (result.state === 'product-mismatch') updateProductMismatch(checkerEl, garageLabel, active);
    else if (result.state === 'engine-unconfirmed') updateEngineUnconfirmed(checkerEl, active);

    showState(checkerEl, result.state);
  }

  function recompute(checkerEl, sectionRoot, opts) {
    var data = getCompatData(checkerEl);
    if (!data) return;

    var currentVariant = null;
    if (window.TrautisVariantReactivity) {
      var sectionId = sectionRoot.getAttribute('data-section-id');
      var apiVariant = window.TrautisVariantReactivity.getCurrentVariant(sectionId);
      if (apiVariant) currentVariant = findVariantById(data, apiVariant.id);
    }
    if (!currentVariant && data.variants.length > 0) currentVariant = data.variants[0];
    if (!currentVariant) return;

    var active = null, garage = null, garageLabel = '';
    if (window.TrautisFitment && typeof window.TrautisFitment.get === 'function') {
      active = window.TrautisFitment.get();
      if (active && active.chassis && active.engine) {
        garage = active.key || (active.chassis + '-' + active.engine);
        garageLabel = active.label || active.chassis;
      } else {
        active = null;
      }
    }

    // No BMW set → unset state. No async needed.
    if (!active) {
      applyResult(checkerEl, { state: 'unset', matchedVariant: null }, currentVariant, garageLabel, null);
      return;
    }

    // BMW set → load the lookup, then run the sync matcher.
    if (!window.TrautisFitment || typeof window.TrautisFitment.loadLookup !== 'function' || typeof window.TrautisFitment.matches !== 'function') {
      applyResult(checkerEl, { state: 'unset', matchedVariant: null }, currentVariant, garageLabel, null);
      return;
    }

    window.TrautisFitment.loadLookup().then(function (lookupData) {
      var matcher = function (gids) {
        return window.TrautisFitment.matches(gids, active, lookupData);
      };
      var result = resolveState(currentVariant, garage, data, matcher);
      applyResult(checkerEl, result, currentVariant, garageLabel, active);
      if (opts && opts.glow) applyGlow(checkerEl, result.state);
    }, function () {
      // Lookup fetch failed — fall back to unset so we don't show a misleading state.
      applyResult(checkerEl, { state: 'unset', matchedVariant: null }, currentVariant, garageLabel, null);
    });
  }

  function initSection(sectionRoot) {
    var checkerEl = sectionRoot.querySelector('[data-compat-checker]');
    if (!checkerEl) return;

    bindSwitchLink(checkerEl, sectionRoot);
    bindBmwModalCta(checkerEl);
    bindChooseEngine(checkerEl);

    var run = function () { recompute(checkerEl, sectionRoot); };

    // Variantenwechsel + Initiallauf: still aktualisieren, KEIN Glow.
    sectionRoot.addEventListener('trautis:variant-change', run);
    // Fahrzeugwahl: aktualisieren UND beruhigend aufleuchten (Ergebnis-Ton).
    document.addEventListener('trautis:bmw-changed', function () {
      recompute(checkerEl, sectionRoot, { glow: true });
    });

    // Initial run after both 2a and 1d have initialised
    run();
  }

  function init() {
    document.querySelectorAll('[data-product-section]').forEach(initSection);
  }

  return {
    init: init,
    resolveState: resolveState,
    describeSiblingDiff: describeSiblingDiff,
    buildFitmentCollectionUrl: buildFitmentCollectionUrl,
    glowToneForState: glowToneForState
  };
});
